import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { WrappedFieldProps } from 'redux-form';
import { formValueSelector, change } from 'redux-form';

import type { RootState } from 'af-reducers';

import type { Props as PlainMultiselectDropdownProps } from 'af-fields/PlainMultiselectDropdown';
import PlainMultiselectDropdown from 'af-fields/PlainMultiselectDropdown';

interface OwnProps extends WrappedFieldProps, PlainMultiselectDropdownProps {
	isStandalone?: boolean;
	withCaret?: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const MultiselectDropdown: React.FC<Props> = (props) => {
	const {
		alwaysShowErrors = false,
		className = 'dropdown-field dropdown-field--multiselect',
		disableErrorMessage = false,
		selected = [],
		valueKey,
		labelKey,
		onValueChange,
		change: _change,
		input: { name },
		meta: { error, warning, touched, form },
	} = props;

	const handleChange = React.useCallback(async (item, event) => {

		const isChecked = event.target.checked;
		const calculatedPropName = valueKey ?? labelKey;
		let newSelected: Metadata[] = [];

		if (isChecked) {
			newSelected = selected.concat(item);
		} else {
			newSelected = selected.filter((_option) => _option[calculatedPropName] !== item[calculatedPropName]);
		}

		_change(form, name, newSelected);

		// on change callback
		await onValueChange?.(newSelected);
	}, [_change, form, labelKey, name, onValueChange, selected, valueKey]);

	const getDropdownClassName = React.useCallback(() => {
		return (touched || alwaysShowErrors) && error ? `${className} dropdown-field--with-error` : className;
	}, [alwaysShowErrors, className, error, touched]);

	const getBlock = React.useCallback(() => {
		return (
			(touched || alwaysShowErrors) && !disableErrorMessage &&
			(
				(error && <span className="help-block"><span className="icon-info" /> {error}</span>) ||
				(warning && <span className="help-block text-orange"><span className="icon-info" /> {warning}</span>)
			)
		);
	}, [alwaysShowErrors, disableErrorMessage, error, touched, warning]);

	const valueChangeHandler = React.useCallback((selectedOption: Nullable<Metadata>) => {
		_change(form, name, selectedOption);
	}, [_change, form, name]);

	return (
		<PlainMultiselectDropdown
			{...props}
			block={getBlock()}
			dropdownClassName={getDropdownClassName()}
			handleChange={handleChange}
			onValueChange={onValueChange ?? valueChangeHandler}
		/>
	);
};

const EMPTY_ARRAY = [];

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { meta: { form }, input: { name } } = ownProps;
	const selector = formValueSelector(form);
	const selected = selector(state, name) || EMPTY_ARRAY;

	return {
		selected,
	};
}

const connector = connect(mapStateToProps, { change });

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(MultiselectDropdown);
